/**
 * Directory sync feature — Zod schemas.
 *
 * A tenant either has no directory provider configured, or
 * exactly one of three: SCIM 2.0, LDAP, or Google Workspace.
 * Schemas model this as a discriminated union on `kind` so
 * field validation adapts to whichever provider the operator
 * picked.
 *
 * The whole provider config is a loose JSON blob on the tenant
 * settings, but the UI enforces strict typing on reads and
 * writes — backend changes that break this schema are a loud
 * frontend crash, not silent corruption.
 */

import { z } from "zod";
import {
  directoryConfigSchema as directoryConnectionConfigSchema,
  directoryStatusSchema,
  directoryOverviewSchema,
  adInboundDirectorySchema,
  adInboundDirectoryPaginationSchema,
  adInboundDirectoriesListResponseSchema,
  adOuEntrySchema,
  adOuListResponseSchema,
  adDirectoryUserLinkSchema,
  adDirectoryUsersResponseSchema,
  adDirectoryGroupLinkSchema,
  adDirectoryGroupsResponseSchema,
  directorySyncRejectSchema,
  directorySyncRejectsResponseSchema,
} from "./connections.generated";
import { directorySyncRunSchema } from "./sync.generated";

// Response shapes are generated from api/openapi/admin.yaml (make docs) and
// re-exported here; this file keeps only the forms, URL state and request
// bodies, and the UI vocabularies derived from the generated enums.
export {
  directoryConnectionConfigSchema,
  directoryStatusSchema,
  directoryOverviewSchema,
  adInboundDirectorySchema,
  adInboundDirectoryPaginationSchema,
  adInboundDirectoriesListResponseSchema,
  adOuEntrySchema,
  adOuListResponseSchema,
  adDirectoryUserLinkSchema,
  adDirectoryUsersResponseSchema,
  adDirectoryGroupLinkSchema,
  adDirectoryGroupsResponseSchema,
  directorySyncRejectSchema,
  directorySyncRejectsResponseSchema,
};

// ── Provider kinds ────────────────────────────────────────────────────────

export const providerKindSchema = directoryStatusSchema.shape.kind;
export type ProviderKind = z.infer<typeof providerKindSchema>;

// ── SCIM config ───────────────────────────────────────────────────────────

export const scimConfigSchema = z.object({
  kind: z.literal("scim"),
  enabled: z.boolean(),
  base_url: z.string().url("Must be a valid URL"),
  /** Bearer token is write-only; reads return null. */
  bearer_token: z.string().nullable(),
  sync_interval_minutes: z.number().int().min(5).max(1440),
  /** The identity source this connection speaks for; set by its own binding, never by a save. */
  identity_source_id: z.string().nullable().optional(),
  include_groups: z.boolean(),
  deactivate_on_remove: z.boolean(),
});
export type ScimConfig = z.infer<typeof scimConfigSchema>;

// ── LDAP config ───────────────────────────────────────────────────────────

export const ldapConfigSchema = z.object({
  kind: z.literal("ldap"),
  enabled: z.boolean(),
  host: z.string().min(1, "Host is required"),
  port: z.number().int().min(1).max(65535),
  use_tls: z.boolean(),
  bind_dn: z.string().min(1, "Bind DN is required"),
  /** Bind password is write-only; reads return null. */
  bind_password: z.string().nullable(),
  user_search_base: z.string().min(1, "User search base is required"),
  // No Zod `.default()` here — it widens the resolver's input type
  // to `undefined`-allowed and breaks react-hook-form's generic.
  // The form's own `defaultConfig("ldap")` provides the filled-in
  // defaults; the server applies its own fallbacks on empty strings.
  user_filter: z.string(),
  attr_email: z.string(),
  attr_display_name: z.string(),
  sync_interval_minutes: z.number().int().min(5).max(1440),
  identity_source_id: z.string().nullable().optional(),
});
export type LdapConfig = z.infer<typeof ldapConfigSchema>;

// ── Google Workspace config ───────────────────────────────────────────────

export const googleWorkspaceConfigSchema = z.object({
  kind: z.literal("google_workspace"),
  enabled: z.boolean(),
  admin_email: z.string().email("Must be a valid email"),
  customer_id: z.string().min(1, "Customer ID is required"),
  /** Service-account JSON is write-only; reads return null. */
  service_account_json: z.string().nullable(),
  sync_interval_minutes: z.number().int().min(15).max(1440),
  identity_source_id: z.string().nullable().optional(),
  include_suspended: z.boolean(),
});
export type GoogleWorkspaceConfig = z.infer<typeof googleWorkspaceConfigSchema>;

// ── Union ────────────────────────────────────────────────────────────────

/** The pull-connection FORM: one variant per provider. The saved
 *  connection is read with the generated `directoryConnectionConfigSchema`
 *  and turned into form values by `directoryConfigToForm`. */
export const directoryConfigFormSchema = z.discriminatedUnion("kind", [
  scimConfigSchema,
  ldapConfigSchema,
  googleWorkspaceConfigSchema,
]);
export type DirectoryConfig = z.infer<typeof directoryConfigFormSchema>;

/** The saved pull connection as the server returns it: one flat object,
 *  with the other kinds' fields and every secret omitted. */
export type DirectoryConnectionConfig = z.infer<typeof directoryConnectionConfigSchema>;

/** Saved connection → form values. Secrets are write-only, so the form
 *  holds null for them (null = keep the saved secret); LDAP fields the server
 *  omits when empty come back as empty strings. */
export function directoryConfigToForm(config: DirectoryConnectionConfig): DirectoryConfig {
  const common = {
    enabled: config.enabled,
    sync_interval_minutes: config.sync_interval_minutes,
    identity_source_id: config.identity_source_id,
  };
  switch (config.kind) {
    case "scim":
      return {
        ...common,
        kind: "scim",
        base_url: config.base_url ?? "",
        bearer_token: null,
        include_groups: config.include_groups ?? false,
        deactivate_on_remove: config.deactivate_on_remove ?? false,
      };
    case "ldap":
      return {
        ...common,
        kind: "ldap",
        host: config.host ?? "",
        port: config.port ?? 636,
        use_tls: config.use_tls ?? true,
        bind_dn: config.bind_dn ?? "",
        bind_password: null,
        user_search_base: config.user_search_base ?? "",
        user_filter: config.user_filter ?? "",
        attr_email: config.attr_email ?? "",
        attr_display_name: config.attr_display_name ?? "",
      };
    case "google_workspace":
      return {
        ...common,
        kind: "google_workspace",
        admin_email: config.admin_email ?? "",
        customer_id: config.customer_id ?? "",
        service_account_json: null,
        include_suspended: config.include_suspended ?? false,
      };
  }
}

// ── Status ────────────────────────────────────────────────────────────────

export const syncRunStatusSchema = directorySyncRunSchema.shape.status;
export type SyncRunStatus = z.infer<typeof syncRunStatusSchema>;
export type DirectoryStatus = z.infer<typeof directoryStatusSchema>;
export type DirectoryOverview = z.infer<typeof directoryOverviewSchema>;

/** POST /directory/sync answers 202 with the refreshed overview, or with the
 *  bare status when the refresh read after queueing failed. */
export const directorySyncTriggerResponseSchema = z.union([directoryOverviewSchema, directoryStatusSchema]);
export type DirectorySyncTriggerResponse = z.infer<typeof directorySyncTriggerResponseSchema>;

// ── AD inbound directory ────────────────────────────────────────────────
//
// A distinct feature from the outbound `DirectoryConfig` above (that's
// PureID polling out to LDAP/SCIM/Google) and from inbound SCIM (a
// generic, IdP-agnostic push protocol). This is AD-specific: an on-prem
// Windows agent (Renegadir-Connect) pushes users/groups from Active
// Directory into this tenant over OAuth2 client-credentials + a fixed
// REST contract. The wire shape mirrors gatekeeper-api's `Directory`
// model exactly — Renegadir-Connect's compiled binary cannot be
// patched, so field-name drift here breaks the agent with no
// workaround.
//
// Deliberately NO expression-language support: no
// `user_external_unique_attribute_transform` field anywhere, and no
// `is_expression`/`is_custom_source` flags on attribute-mapping rows.

export const adDirectorySyncStatusSchema = adInboundDirectorySchema.shape.sync_status;
export type AdDirectorySyncStatus = z.infer<typeof adDirectorySyncStatusSchema>;

// Flat {target, source} row — target is one of PureID's own user
// fields, source is the AD attribute name Renegadir-Connect reads.
// No expression mode, no custom-source flag (scope cut).
//
// This row shape is a FORM-ONLY model: `useFieldArray` needs an ordered
// array to render and reorder, but the wire (and the DB column, and
// gatekeeper's own `Directory.attribute_mapping`) is a flat
// `{pureid_field: ad_attribute}` OBJECT. The two are converted at the
// query boundary by `attributeMappingToRows` / `attributeMappingToWire`
// below — never send rows to the server.
export const attributeMappingRowSchema = z.object({
  target: z.string().min(1, "Target field is required"),
  source: z.string().min(1, "Source attribute is required"),
});
export type AttributeMappingRow = z.infer<typeof attributeMappingRowSchema>;

/** Wire shape of `attribute_mapping`: `{pureid_field: ad_attribute}`. */
export const attributeMappingWireSchema = z.record(z.string(), z.string());
export type AttributeMappingWire = z.infer<typeof attributeMappingWireSchema>;

/** Wire object → ordered form rows (insertion order is preserved by JS
 *  string-keyed objects, so a save/reload round-trip keeps row order). */
export function attributeMappingToRows(mapping: AttributeMappingWire): AttributeMappingRow[] {
  return Object.entries(mapping).map(([target, source]) => ({ target, source }));
}

/** Form rows → wire object. Duplicate targets are rejected by the write
 *  schema's `superRefine` before this ever runs, so last-write-wins here
 *  is unreachable rather than silent. */
export function attributeMappingToWire(rows: readonly AttributeMappingRow[]): AttributeMappingWire {
  const out: Record<string, string> = {};
  for (const row of rows) out[row.target] = row.source;
  return out;
}

// Same distinguished-name path shape gatekeeper's AD setup form
// validates against: LDAP://host/OU=...,DC=...
export const AD_LDAP_PATH_REGEX =
  /^LDAP:\/\/[a-z0-9.-]+\/(?:CN|OU|DC)=[^,]+(?:,(?:CN|OU|DC)=[^,]+)*$/i;

export const adDirectorySettingsSchema = z.object({
  ldap_path: z
    .string()
    .min(1, "LDAP path is required")
    .regex(
      AD_LDAP_PATH_REGEX,
      "Must be a valid LDAP path (e.g. LDAP://dc.example.com/OU=Users,DC=example,DC=com)",
    ),
  full_sync: z.boolean(),
  user_organizational_units: z.array(z.string()),
  group_organizational_units: z.array(z.string()),
  // No match_user_by / allowed_domains /
  // replace_invalid_group_name_characters_with: all three are
  // OUTBOUND-only (PureID → AD) in both gatekeeper and the connector, and
  // this surface is inbound-only. Inbound matching is the server's job —
  // external_id link first, then user_external_unique_attribute. See the
  // Go `DirectorySettings` doc comment for the per-field rationale.
});
export type AdDirectorySettings = z.infer<typeof adDirectorySettingsSchema>;

/** Write-once/reveal-once: `client_secret` is present only on the create and
 *  rotate-credentials responses. Never cached — see
 *  `useRotateAdDirectoryCredentialsMutation`. */
export const adOauthApplicationSchema = adInboundDirectorySchema.shape.oauth_application;
export type AdOauthApplication = z.infer<typeof adOauthApplicationSchema>;

/**
 * How far a directory may go when it reconciles group membership.
 *
 * `scim_group_members` has many writers and is read on the authorization
 * hot path, so a directory that reconciles a group against its own member
 * list without knowing which rows it owns deletes everyone else's. Renegadir
 * shipped exactly that and had to disable inbound membership sync in
 * production; this setting is what lets the capability exist safely.
 *
 * Ordered by blast radius, least to most.
 */
export const groupMembershipModeSchema = adInboundDirectorySchema.shape.group_membership_mode;
export type GroupMembershipMode = z.infer<typeof groupMembershipModeSchema>;

export type AdInboundDirectory = z.infer<typeof adInboundDirectorySchema>;
export type AdInboundDirectoriesListResponse = z.infer<typeof adInboundDirectoriesListResponseSchema>;

// ── Write schema (create + update) ──────────────────────────────────────
//
// This is the FORM model, not the wire model: `attribute_mapping` is an
// ordered row array here because that is what the editor needs, while
// the server takes a flat object. `toAdInboundDirectoryPayload` below is
// the single conversion point, and every mutation goes through it.
//
// Everything except server-derived fields (id, sync_status,
// oauth_application, timestamps). No
// `user_external_unique_attribute_transform`, no expression flags.
export const adInboundDirectoryWriteSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Maximum 100 characters"),
  enabled: z.boolean(),
  sync_interval_seconds: z.number().int().min(60, "Minimum 60 seconds").max(86_400),
  // 0 is the "never run a full sync" sentinel (gatekeeper's own default
  // for inbound AD); any other value must be a real interval. The column
  // CHECK is `>= 0` to allow it.
  full_sync_interval_seconds: z
    .number()
    .int()
    .max(604_800, "Maximum 604800 seconds")
    .refine((v) => v === 0 || v >= 300, "Use 0 to disable full sync, or at least 300 seconds"),
  user_external_unique_attribute: z.string().min(1, "Required"),
  // No `.default()` — Zod 4 would widen the input type to allow undefined,
  // which breaks RHF's SubmitHandler generic. The default lives in the
  // form's defaultValues instead (the safe one, "directory_managed").
  group_membership_mode: groupMembershipModeSchema,
  attribute_mapping: z.array(attributeMappingRowSchema).superRefine((rows, ctx) => {
    // Two rows targeting the same PureID field would collapse into one
    // key on the wire, silently discarding whichever the operator
    // entered first. Reject it at the row that duplicates.
    const seen = new Map<string, number>();
    rows.forEach((row, index) => {
      const first = seen.get(row.target);
      if (first === undefined) {
        seen.set(row.target, index);
        return;
      }
      ctx.addIssue({
        code: "custom",
        path: [index, "target"],
        message: "This PureID field is already mapped",
      });
    });
  }),
  directory_settings: adDirectorySettingsSchema,
});
export type AdInboundDirectoryWriteInput = z.infer<typeof adInboundDirectoryWriteSchema>;

/** Form values → the JSON body `createDirectoryRequest` /
 *  `updateDirectoryRequest` (backend `admin_handler.go`) decode. The only
 *  structural difference is `attribute_mapping`; everything else already
 *  matches the wire field-for-field. */
export function toAdInboundDirectoryPayload(input: AdInboundDirectoryWriteInput) {
  return {
    ...input,
    attribute_mapping: attributeMappingToWire(input.attribute_mapping),
  };
}

// ── Rotate-credentials (reveal-once) response ───────────────────────────
//
// The rotate endpoint returns a full directory record whose
// `oauth_application.client_secret` is populated exactly once; this is
// the narrowed shape the reveal panel consumes, projected in
// `useRotateAdDirectoryCredentialsMutation` so the plaintext secret never
// travels inside a cacheable directory object.

export interface AdDirectoryCredentials {
  client_id: string;
  client_secret: string;
}

// ── OU catalog (session cache the connector last synced) ────────────────
//
// The connector reports two independently-scoped OU lists (users vs.
// groups) — see `ouSyncRequest` in connector_handler.go — and the two OU
// pickers must not offer each other's scope.

export type AdOuEntry = z.infer<typeof adOuEntrySchema>;

// ONE catalog, not one per scope. An AD forest has a single OU tree; what
// differs between the user and group scopes is which OUs the operator SELECTS
// (user_organizational_units / group_organizational_units), not which exist.
// The connector only ever reports one list, and Renegadir renders both of its
// pickers from one fetchDirectoryOUList response too.
export type AdOuListResponse = z.infer<typeof adOuListResponseSchema>;

// ── Synced user / group link rows ───────────────────────────────────────
//
// Both link tables (ad_directory_users, ad_directory_groups) share the
// same sync_status vocabulary.
export const adDirectoryLinkSyncStatusSchema = adDirectoryUserLinkSchema.shape.sync_status;
export type AdDirectoryLinkSyncStatus = z.infer<typeof adDirectoryLinkSyncStatusSchema>;

/** Query-shape filter for the Users/Groups tab fetchers — decoupled from
 *  the URL search-param names (see ad-inbound-users-search.ts /
 *  ad-inbound-groups-search.ts), which are prefixed to avoid the two
 *  tabs' filters colliding in the URL. */
// A `type` (not `interface`) so it structurally satisfies
// `Readonly<Record<string, unknown>>` at the query-keys call site —
// TS only infers an implicit index signature for object type
// aliases/literals, not for interfaces.
export type AdDirectoryLinksFilter = {
  offset?: number;
  sync_status?: AdDirectoryLinkSyncStatus;
  q?: string;
};

export type AdDirectoryUserLink = z.infer<typeof adDirectoryUserLinkSchema>;
export type AdDirectoryUsersResponse = z.infer<typeof adDirectoryUsersResponseSchema>;
// ad_group_scope / ad_group_type are the values the agent reported, which the
// server does not validate on read: they are plain strings, never a closed enum
// that would fail the whole page on one unexpected value.
export type AdDirectoryGroupLink = z.infer<typeof adDirectoryGroupLinkSchema>;
export type AdDirectoryGroupsResponse = z.infer<typeof adDirectoryGroupsResponseSchema>;

// ── Rejected records ────────────────────────────────────────────────────
//
// Records that failed BEFORE resolving to a user or group. The link tables
// structurally cannot hold these — their user_id/group_id are NOT NULL — so
// without this surface, "this person is in AD and is not in PureID, why?" had
// no answer anywhere in the product.

// In lockstep with directorysync.RejectReason; TestConsoleKnowsEveryRejectReason
// in backend/internal/tenant/directorysync holds the two together, because an
// unknown reason fails the whole rejects list.
export const directorySyncRejectReasonSchema = z.enum([
  "missing_unique_attribute",
  "unsupported_unique_attribute",
  "ambiguous_match",
  "user_create_failed",
  "group_create_failed",
  "invalid_payload",
  "adoption_requires_review",
]);
export type DirectorySyncRejectReason = z.infer<typeof directorySyncRejectReasonSchema>;

/** Operator-facing copy for each reason, in sentence case. Keyed by the
 *  backend's enum so an unhandled value is a type error, not a blank cell. */
export const DIRECTORY_SYNC_REJECT_LABELS: Record<DirectorySyncRejectReason, string> = {
  missing_unique_attribute: "No value for the matching attribute",
  unsupported_unique_attribute: "Matching attribute not supported",
  ambiguous_match: "Matched more than one user",
  user_create_failed: "Could not create the user",
  group_create_failed: "Could not create the group",
  invalid_payload: "Record could not be read",
  adoption_requires_review: "Matched a person who needs review",
};

export type DirectorySyncReject = z.infer<typeof directorySyncRejectSchema>;
export type DirectorySyncRejectsResponse = z.infer<typeof directorySyncRejectsResponseSchema>;

// The literal list above is read by the backend parity test; this holds it to
// the contract's vocabulary at compile time in both directions.
type ContractRejectReason = z.infer<typeof directorySyncRejectSchema.shape.reason_code>;
const rejectReasonsMatchContract: [DirectorySyncRejectReason, ContractRejectReason] extends [ContractRejectReason, DirectorySyncRejectReason] ? true : never = true;
void rejectReasonsMatchContract;

export type DirectorySyncRejectsFilter = {
  offset?: number;
  object_type?: "user" | "group";
  reason?: DirectorySyncRejectReason;
  q?: string;
};

// ── Workday directories ─────────────────────────────────────────────────
//
// The HTTP contract is api/openapi/admin.yaml (TenantWorkday*). A Workday
// directory is read by PureID from a custom report with an integration
// user's credential, which is described here and never returned.

export const workdayCredentialSchema = z.object({
  id: z.string(),
  sealed_for_origin: z.string(),
  auth_scheme: z.string(),
  entered_at: z.string(),
  entered_by: z.string().nullable(),
});

export const workdaySourceSchema = z.object({
  report_url: z.string(),
  destination_origin: z.string(),
  identity_source_id: z.string().nullable(),
  worker_id_field: z.string(),
  status_field: z.string(),
  active_values: z.array(z.string()),
  inactive_values: z.array(z.string()),
  credential: workdayCredentialSchema.nullable(),
  pull: z.object({
    attempted_at: z.string().nullable(),
    failures: z.number().int().nonnegative(),
    held: z.boolean(),
    running: z.boolean(),
    deprovisions_to_confirm: z.number().int().positive().nullable(),
  }),
});
export type WorkdaySource = z.infer<typeof workdaySourceSchema>;

export const workdayDirectorySchema = adInboundDirectorySchema.extend({
  directory_type: z.literal("workday"),
  workday: workdaySourceSchema,
});
export type WorkdayDirectory = z.infer<typeof workdayDirectorySchema>;

export const workdayProbeResultSchema = z.object({
  workers: z.number().int().nonnegative(),
  fields: z.array(z.string()),
});
export type WorkdayProbeResult = z.infer<typeof workdayProbeResultSchema>;

/** How often a report is read. The server accepts 15 minutes to 7 days. */
export const WORKDAY_SCHEDULES = [
  { seconds: 900, label: "Every 15 minutes" },
  { seconds: 3600, label: "Every hour" },
  { seconds: 14_400, label: "Every 4 hours" },
  { seconds: 86_400, label: "Every day" },
] as const;

const WORKDAY_HOST = /^https:\/\/[a-z0-9.-]+\.(?:my)?workday\.com(?::443)?(?:\/[^?#]*)?\/ccx\/service\/customreport2\//i;

export const workdayCreateFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Maximum 100 characters"),
  report_url: z
    .string()
    .trim()
    .min(1, "The report URL is required")
    .max(2048)
    .regex(WORKDAY_HOST, "Use the report's web service URL on your workday.com or myworkday.com host (…/ccx/service/customreport2/…)"),
  identity_source_id: z.string().min(1, "Choose the identity source this directory speaks for"),
  sync_interval_seconds: z.number().int().min(900).max(604_800),
});
export type WorkdayCreateForm = z.infer<typeof workdayCreateFormSchema>;

export const workdayCredentialFormSchema = z.object({
  username: z
    .string()
    .min(1, "The integration user is required")
    .max(256)
    .refine((v) => !v.includes(":"), "The user name cannot contain a colon"),
  password: z.string().min(1, "The password is required").max(1024),
});

/** Status values are typed as a comma-separated list. */
export function parseStatusValues(raw: string): string[] {
  return raw
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

/** PureID attributes a Workday report field may be mapped to. Mirrors
 *  userTargetSetters and identifierTargetKeys in backend matching.go; the
 *  server refuses anything else. */
export const WORKDAY_TARGETS: readonly { target: string; label: string }[] = [
  { target: "employee_id", label: "Employee number" },
  { target: "work_email", label: "Work email" },
  { target: "username", label: "Username" },
  { target: "mobile_number", label: "Mobile number" },
  { target: "first_name", label: "First name" },
  { target: "middle_name", label: "Middle name" },
  { target: "last_name", label: "Last name" },
  { target: "nickname", label: "Preferred name" },
  { target: "job_title", label: "Job title" },
  { target: "department", label: "Department" },
  { target: "division", label: "Division" },
  { target: "cost_center", label: "Cost center" },
  { target: "company", label: "Company" },
  { target: "employee_type", label: "Worker type" },
  { target: "manager_id", label: "Manager's employee number" },
  { target: "manager_display_name", label: "Manager's name" },
  { target: "locale", label: "Locale" },
  { target: "timezone", label: "Time zone" },
];
